import {
  Box, Button, Flex, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td,
  Text, Th, Thead, Tr, useColorModeValue, useDisclosure, useToast, FormControl,
  FormLabel, VStack, Badge, Spinner, HStack, Image, Checkbox,
  InputGroup, InputRightElement, Textarea,
} from '@chakra-ui/react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import ProductTableActionsMenu from 'components/menu/ProductTableActionsMenu';
import * as React from 'react';
import {
  MdImage, MdAdd, MdClose, MdCameraAlt,
} from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import {
  collection, getDocs, getFirestore, query, orderBy, addDoc,
  updateDoc, deleteDoc, doc, getCountFromServer,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import app from '../../../../config/firebaseConfig';

const db = getFirestore(app);
const storage = getStorage(app);

const columnHelper = createColumnHelper();

// ── Register a numeric px Size format (default Quill only ships
//    small/normal/large/huge — this swaps it for actual point sizes) ──────
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '40px', '48px'];
Quill.register(Size, true);

// ── Shared rich text editor config — same as the pooja description field ──
const QUILL_TOOLBAR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link',
];

export default function ProductTable(props) {
  const [data, setData] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [sorting, setSorting] = React.useState([]);
  const [categories, setCategories] = React.useState([]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);

  const [loading, setLoading] = React.useState(false);
  const [imageUploading, setImageUploading] = React.useState(false);

  // Incrementing this forces a fresh Firestore fetch
  const [refreshKey, setRefreshKey] = React.useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [editingRow, setEditingRow] = React.useState(null);
  const [selectedImageFile, setSelectedImageFile] = React.useState(null);
  const [isCustomCategory, setIsCustomCategory] = React.useState(false);
  const [customCategoryValue, setCustomCategoryValue] = React.useState('');
  const [formData, setFormData] = React.useState({
    name: '',
    desc: '',
    originalPrice: null,
    price: 0,
    image: '',
    badge: null,
    link: 'single.html',
    category: '',
    type: ['bestseller'],
  });

  const [badgeEnabled, setBadgeEnabled] = React.useState(false);
  const [badgeText, setBadgeText] = React.useState('');
  const [badgePosition, setBadgePosition] = React.useState('right');
  const [badgeType, setBadgeType] = React.useState('red');

  const typeOptions = ['bestseller', 'new'];
  const fileInputRef = React.useRef(null);

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // ─── Always fetches fresh from Firestore (no local cache) ──────────
  const fetchProducts = React.useCallback(async () => {
    setLoading(true);
    try {
      // 1. Count
      const productsRef = collection(db, 'products');
      const countSnap = await getCountFromServer(productsRef);
      const total = countSnap.data().count;
      setTotalCount(total);
      const pages = Math.max(1, Math.ceil(total / pageSize));
      setTotalPages(pages);

      // 2. All docs ordered by updatedAt desc
      const q = query(productsRef, orderBy('updatedAt', 'desc'));
      const snap = await getDocs(q);
      const allProducts = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        type: Array.isArray(d.data().type) ? d.data().type : [],
      }));

      // 3. Categories
      setCategories([...new Set(allProducts.map((p) => p.category).filter(Boolean))]);

      // 4. Paginate client-side
      const start = (currentPage - 1) * pageSize;
      setData(allProducts.slice(start, start + pageSize));
    } catch (error) {
      console.error('fetchProducts error:', error);
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, refreshKey]); // refreshKey is the key fix

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  // ─── Image ─────────────────────────────────────────────────────────
  const handleImageSelection = (file) => {
    if (!file) return;
    setSelectedImageFile(file);
    setFormData((prev) => ({ ...prev, image: URL.createObjectURL(file) }));
  };

  const uploadImageToStorage = async (file) => {
    const storageRef = ref(storage, `products/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // ─── Edit ──────────────────────────────────────────────────────────
  const handleEdit = (row) => {
    setEditingRow(row);
    setFormData({
      name: row.name || '',
      desc: row.desc || '',
      originalPrice: row.originalPrice || null,
      price: row.price || 0,
      image: row.image || '',
      badge: row.badge || null,
      link: row.link || 'single.html',
      category: row.category || '',
      type: Array.isArray(row.type) ? row.type : [],
    });

    if (row.category && !categories.includes(row.category)) {
      setIsCustomCategory(true);
      setCustomCategoryValue(row.category);
    } else {
      setIsCustomCategory(false);
      setCustomCategoryValue('');
    }

    if (row.badge) {
      setBadgeEnabled(true);
      setBadgeText(row.badge.text || '');
      setBadgePosition(row.badge.position || 'right');
      setBadgeType(row.badge.type || 'red');
    } else {
      setBadgeEnabled(false);
      setBadgeText('');
      setBadgePosition('right');
      setBadgeType('red');
    }

    setSelectedImageFile(null);
    onOpen();
  };

  // ─── Delete ────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      toast({ title: 'Deleted successfully', status: 'success', duration: 3000, isClosable: true });
      triggerRefresh();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ─── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setImageUploading(true);
    try {
      // Upload new image if selected
      let imageUrl = formData.image;
      if (selectedImageFile) {
        imageUrl = await uploadImageToStorage(selectedImageFile);
      }

      const badge = badgeEnabled
        ? { text: badgeText, position: badgePosition, type: badgeType }
        : null;

      const categoryValue = isCustomCategory ? customCategoryValue : formData.category;

      const productData = {
        ...formData,
        image: imageUrl,
        category: categoryValue,
        badge,
        type: Array.isArray(formData.type) ? formData.type : [],
        updatedAt: new Date(),
      };

      if (editingRow) {
        await updateDoc(doc(db, 'products', String(editingRow.id)), productData);
        toast({ title: 'Product updated', status: 'success', duration: 3000, isClosable: true });
      } else {
        await addDoc(collection(db, 'products'), productData);
        toast({ title: 'Product created', status: 'success', duration: 3000, isClosable: true });
        setCurrentPage(1); // go to first page so new item is visible
      }

      onClose();
      // Wait a tick for Firestore to propagate then refresh
      setTimeout(triggerRefresh, 600);
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setImageUploading(false);
      setSelectedImageFile(null);
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────
  const handleTypeChange = (type) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.type) ? prev.type : [];
      return {
        ...prev,
        type: current.includes(type) ? current.filter((t) => t !== type) : [...current, type],
      };
    });
  };

  const handleCategoryChange = (value) => {
    if (value === 'custom') {
      setIsCustomCategory(true);
      setCustomCategoryValue('');
      setFormData((prev) => ({ ...prev, category: '' }));
    } else {
      setIsCustomCategory(false);
      setCustomCategoryValue('');
      setFormData((prev) => ({ ...prev, category: value }));
    }
  };

  const handleCustomCategoryChange = (value) => {
    setCustomCategoryValue(value);
    setFormData((prev) => ({ ...prev, category: value }));
  };

  // ─── Columns ───────────────────────────────────────────────────────
  const columns = [
    columnHelper.accessor('image', {
      id: 'image',
      header: () => <Icon as={MdImage} w="24px" h="24px" color={textColor} />,
      cell: (info) => (
        <Flex justify="center" align="center">
          <Image
            src={info.getValue()}
            w="50px"
            h="50px"
            borderRadius="full"
            objectFit="cover"
            border="2px solid"
            borderColor={borderColor}
            fallbackSrc="https://via.placeholder.com/50"
          />
        </Flex>
      ),
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <Text color={textColor}>Name</Text>,
      cell: (info) => <Text color={textColor} fontWeight="bold">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('desc', {
      id: 'desc',
      header: () => <Text color={textColor}>Description</Text>,
      cell: (info) => {
        // desc is now stored as HTML — strip tags for a compact table preview
        const raw = info.getValue() || '';
        const plain = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        return (
          <Text color={textColor} maxW="200px" noOfLines={2} title={plain}>
            {plain || '—'}
          </Text>
        );
      },
    }),
    columnHelper.accessor('price', {
      id: 'price',
      header: () => <Text color={textColor}>Price</Text>,
      cell: (info) => <Text color={textColor}>₹{info.getValue()}</Text>,
    }),
    columnHelper.accessor('originalPrice', {
      id: 'originalPrice',
      header: () => <Text color={textColor}>Original Price</Text>,
      cell: (info) => (
        <Text color={textColor}>{info.getValue() ? `₹${info.getValue()}` : 'N/A'}</Text>
      ),
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: () => <Text color={textColor}>Type</Text>,
      cell: (info) => {
        const types = Array.isArray(info.getValue()) ? info.getValue() : [];
        return (
          <HStack flexWrap="wrap">
            {types.map((t) => <Badge key={t} colorScheme="purple">{t}</Badge>)}
          </HStack>
        );
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => <Text color={textColor}>Actions</Text>,
      cell: (info) => (
        <ProductTableActionsMenu onEdit={() => handleEdit(info.row.original)} />
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Product Management
          </Text>
          <Button
            leftIcon={<Icon as={MdAdd} />}
            colorScheme="brand"
            onClick={() => {
              setEditingRow(null);
              setFormData({
                name: '', desc: '', originalPrice: null, price: 0,
                image: '', badge: null, link: 'single.html', category: '', type: ['bestseller'],
              });
              setBadgeEnabled(false);
              setBadgeText('');
              setBadgePosition('right');
              setBadgeType('red');
              setSelectedImageFile(null);
              setIsCustomCategory(false);
              setCustomCategoryValue('');
              onOpen();
            }}
          >
            Add Product
          </Button>
        </Flex>

        <Box>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((hg) => (
                <Tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <Th key={h.id} borderColor={borderColor}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={columns.length}>
                    <Flex justify="center" py="10"><Spinner /></Flex>
                  </Td>
                </Tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length}>
                    <Text textAlign="center" py="10">No data available</Text>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} borderColor="transparent">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex px="25px" justify="space-between" align="center" pb="16px">
          <Flex align="center" gap="10px">
            <Text fontSize="sm">Show</Text>
            <Select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              w="70px"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </Select>
            <Text fontSize="sm">entries</Text>
          </Flex>
          <Text fontSize="sm">Total: {totalCount}</Text>
          <Flex align="center" gap="10px">
            <IconButton
              icon={<Icon as={IoChevronBack} />}
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              isDisabled={currentPage === 1}
            />
            <Text fontSize="sm">Page {currentPage} of {totalPages}</Text>
            <IconButton
              icon={<Icon as={IoChevronForward} />}
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            />
          </Flex>
        </Flex>
      </Card>

      {/* ── Modal ── */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRow ? 'Edit Product' : 'Add Product'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="70vh" overflowY="auto">
            <VStack spacing={4} align="stretch">

              {/* Image Upload */}
              <FormControl>
                <Flex justify="center" align="center" minH="150px">
                  <Box
                    position="relative"
                    onClick={() => fileInputRef.current.click()}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                    transition="opacity 0.2s"
                  >
                    {formData.image ? (
                      <Image
                        src={formData.image}
                        boxSize="100px"
                        borderRadius="full"
                        objectFit="cover"
                      />
                    ) : (
                      <Flex
                        w="100px" h="100px" borderRadius="full"
                        border="2px dashed" borderColor="gray.300"
                        align="center" justify="center" bg="gray.50"
                      >
                        <Icon as={MdImage} w="30px" h="30px" color="gray.400" />
                      </Flex>
                    )}
                    <Flex
                      position="absolute" bottom="0" right="0"
                      bg="brand.500" color="white" borderRadius="full"
                      w="32px" h="32px" align="center" justify="center"
                      border="2px solid white"
                    >
                      <Icon as={MdCameraAlt} w="16px" h="16px" />
                    </Flex>
                  </Box>
                </Flex>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelection(e.target.files[0])}
                  display="none"
                />
              </FormControl>

              {/* Name */}
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter product name"
                />
              </FormControl>

              {/* Description — rich text: headers, numeric px size, color,
                  background, bold/italic/underline/strike, sub/superscript,
                  lists, indent, align, blockquote, code block, link */}
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Box border="1px solid" borderColor={borderColor} borderRadius="8px" overflow="hidden"
                  sx={{
                    '.ql-toolbar': { borderTop: 'none', borderLeft: 'none', borderRight: 'none', bg: 'gray.50', flexWrap: 'wrap' },
                    '.ql-container': { border: 'none', fontSize: '14px', minHeight: '140px' },
                    '.ql-editor': { minHeight: '140px' },
                    // Show plain numbers (e.g. "18") instead of "18px" in the size dropdown
                    '.ql-picker.ql-size .ql-picker-label::before': { content: 'attr(data-value)' },
                    '.ql-picker.ql-size .ql-picker-item::before': { content: 'attr(data-value)' },
                  }}>
                  <ReactQuill
                    value={formData.desc}
                    onChange={val => setFormData(p => ({ ...p, desc: val }))}
                    modules={QUILL_TOOLBAR_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Product description..."
                    theme="snow"
                  />
                </Box>
              </FormControl>

              {/* Price */}
              <FormControl>
                <FormLabel>Price</FormLabel>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="Enter price"
                />
              </FormControl>

              {/* Original Price */}
              <FormControl>
                <FormLabel>
                  Original Price{' '}
                  <Text as="span" color="gray.400" fontSize="sm">(optional)</Text>
                </FormLabel>
                <Input
                  type="number"
                  value={formData.originalPrice || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      originalPrice: e.target.value ? parseFloat(e.target.value) : null,
                    })
                  }
                  placeholder="Enter original price"
                />
              </FormControl>

              {/* Product Type */}
              {/* <FormControl>
                <FormLabel>Product Type</FormLabel>
                <HStack>
                  {typeOptions.map((type) => (
                    <Checkbox
                      key={type}
                      isChecked={formData.type.includes(type)}
                      onChange={() => handleTypeChange(type)}
                    >
                      {type}
                    </Checkbox>
                  ))}
                </HStack>
              </FormControl> */}

            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSubmit} isLoading={imageUploading}>
              {editingRow ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}